import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { publicProductService, publicCategoryService } from '../services/publicApi';
import './Products.css';
// Material UI
import { 
  Row, Col, Card as AntCard, Typography as AntTypography, Input, Switch, 
  Select as AntSelect, Button as AntButton, Tag, Pagination as AntPagination, 
  Space, Popover, Drawer, Badge, Divider, Collapse
} from 'antd';
import SimplePagination from '../components/SimplePagination';
import { 
  SearchOutlined, FilterOutlined, ClearOutlined, 
  DownOutlined, CloseOutlined, ShopOutlined 
} from '@ant-design/icons';

const { Title, Text, Paragraph } = AntTypography;
const { Panel } = Collapse;

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    inStock: false,
    maxPrice: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    brand: '',
    system: '',
    subcategory: '',
    gasType: '',
    detectorType: '',
    usage: '',
    connectivity: '',
    // Champs spécifiques fréquents (pris en charge côté backend)
    phase: '',
    puissance: ''
  });
  const [facets, setFacets] = useState({ 
    brand: [], system: [], gasType: [], detectorType: [], 
    usage: [], connectivity: [], functionality: [] 
  });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categorySpecificFields, setCategorySpecificFields] = useState({});
  // Helper: parse specificFields (peut être string JSON ou objet)
  const parseSpecificFields = (sf) => {
    if (!sf) return {};
    if (typeof sf === 'string') {
      try { const parsed = JSON.parse(sf); return (typeof parsed === 'object' && parsed !== null) ? parsed : {}; } catch(_) { return {}; }
    }
    return (typeof sf === 'object' && sf !== null) ? sf : {};
  };

  const [query, setQuery] = useState('');
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [collapseOpen, setCollapseOpen] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 12
  });

// 🧭 Charger les filtres depuis l'URL au chargement ou changement d'URL
useEffect(() => {
  const searchQuery = searchParams.get('search') || '';
  const categoryQuery = searchParams.get('category') || '';
  const brandQ = searchParams.get('brand') || '';
  const systemQ = searchParams.get('system') || '';
  const gasTypeQ = searchParams.get('gasType') || '';
  const subcategoryQ = searchParams.get('subcategory') || '';
  const detectorTypeQ = searchParams.get('detectorType') || '';
  const usageQ = searchParams.get('usage') || '';
  const connectivityQ = searchParams.get('connectivity') || '';
  // Champs spécifiques possibles dans l'URL
  const phaseQ = searchParams.get('phase') || '';
  const puissanceQ = searchParams.get('puissance') || '';
  const exactQ = searchParams.get('exact') || '';


  // ✅ On initialise la barre de recherche seulement une fois (au montage ou changement d'URL)
  setQuery(searchQuery);

  // ✅ On met à jour les filtres en conséquence
  setFilters(prev => ({
    ...prev,
    search: searchQuery,
    category: categoryQuery,
    brand: brandQ,
    system: systemQ,
    subcategory: subcategoryQ,
    gasType: gasTypeQ,
    detectorType: detectorTypeQ,
    usage: usageQ,
    connectivity: connectivityQ,
    phase: phaseQ,
    puissance: puissanceQ
  }));

  // ✅ Si une catégorie est présente dans l'URL et que les catégories sont chargées,
  // sélectionner la catégorie et pré-remplir les champs spécifiques
  if (categoryQuery && categories && categories.length > 0) {
    const categoryFromUrl = categories.find(c => c.slug === categoryQuery);
    if (categoryFromUrl) {
      try {
        console.log('🔄 Catégorie trouvée depuis URL:', categoryFromUrl);
        console.log('📋 specificFields de la catégorie (URL):', parseSpecificFields(categoryFromUrl.specificFields));
      } catch {}
      setSelectedCategory(categoryFromUrl);
      if (parseSpecificFields(categoryFromUrl.specificFields)) {
        setCategorySpecificFields(parseSpecificFields(categoryFromUrl.specificFields));
        // ✅ Récupérer les valeurs de filtres spécifiques depuis l'URL
        const withSpecifics = { ...filters };
        Object.keys(parseSpecificFields(categoryFromUrl.specificFields) || {}).forEach((fieldKey) => {
          const val = searchParams.get(fieldKey) || '';
          if (val !== '') {
            withSpecifics[fieldKey] = val;
          }
        });
        setFilters(withSpecifics);
      } else {
        setCategorySpecificFields({});
      }
    }
  }
}, [searchParams.toString(), categories.length]);

// ✅ Fonction manuelle pour lancer la recherche
const handleSearch = () => {
  const searchValue = query.trim();
  setFilters(prev => ({ ...prev, search: searchValue }));
  setPagination(prev => ({ ...prev, currentPage: 1 }));
  
  // Synchroniser l'URL avec les paramètres de recherche
  const next = new URLSearchParams(searchParams);
  if (searchValue) {
    next.set('search', searchValue);
  } else {
    next.delete('search');
  }
  // Ne pas forcer exact=1 automatiquement, laisser l'utilisateur choisir
  setSearchParams(next);
};

// ✅ Debounce pour la recherche automatique
useEffect(() => {
  const searchValue = query.trim();
  const timer = setTimeout(() => {
    // Ne mettre à jour que si la valeur a changé
    if (searchValue !== filters.search) {
      setFilters(prev => ({ ...prev, search: searchValue }));
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      
      // Synchroniser l'URL
      const next = new URLSearchParams(searchParams);
      if (searchValue) {
        next.set('search', searchValue);
      } else {
        next.delete('search');
      }
      setSearchParams(next);
    }
  }, 500); // 500ms de délai pour éviter trop de requêtes
  
  return () => clearTimeout(timer);
}, [query, filters.search, searchParams]);

// 📡 Charger les données quand filters ou pagination changent
// Créer une clé de dépendance pour tous les filtres spécifiques à la catégorie
const categorySpecificFiltersKey = useMemo(() => {
  if (!categorySpecificFields || Object.keys(categorySpecificFields).length === 0) {
    return '';
  }
  // Créer une clé basée sur les valeurs actuelles des filtres spécifiques
  const specificKeys = Object.keys(categorySpecificFields);
  const specificValues = specificKeys
    .map(key => `${key}:${filters[key] || ''}`)
    .sort()
    .join('|');
  return specificValues;
}, [
  categorySpecificFields,
  // Utiliser JSON.stringify pour capturer dynamiquement tous les filtres spécifiques
  JSON.stringify(
    Object.keys(categorySpecificFields || {}).reduce((acc, key) => {
      acc[key] = filters[key] || '';
      return acc;
    }, {})
  )
]);

// Clé dépendance pour tous les filtres cat_*
const catFiltersKey = useMemo(() => {
  return Object.entries(filters)
    .filter(([k]) => k.startsWith('cat_'))
    .map(([k, v]) => `${k}=${v || ''}`)
    .sort()
    .join('|');
}, [filters]);

useEffect(() => {
  // S'assurer que les filtres sont initialisés avant de charger les données
  if (filters && Object.keys(filters).length > 0) {
    // Petit délai pour s'assurer que les filtres sont bien mis à jour
    const timer = setTimeout(() => {
      loadData();
    }, 100);
    return () => clearTimeout(timer);
  }
}, [
  filters.search,
  filters.category,
  filters.brand,
  filters.system,
  filters.subcategory,
  filters.gasType,
  filters.detectorType,
  filters.usage,
  filters.connectivity,
  filters.phase,
  filters.puissance,
  filters.inStock,
  filters.maxPrice,
  filters.sortBy,
  filters.sortOrder,
  pagination.currentPage,
  categorySpecificFiltersKey,
  catFiltersKey
]);


  // Charger les catégories une seule fois
  useEffect(() => {
    (async () => {
      try {
        const categoriesResponse = await publicCategoryService.getCategories();
        setCategories(categoriesResponse.categories || []);
        const facetsResponse = await publicProductService.getFacets();
        setFacets(facetsResponse.facets || {});
      } catch (err) {
        console.error('Erreur lors du chargement des catégories:', err);
      }
    })();
  }, []);

  const rootCategories = useMemo(() => (categories || []).filter(c => !c.parentCategoryId && !c.parentCategory), [categories]);

  // Helper: enfants directs d'une catégorie
  const getChildren = (cat) => {
    if (!cat) return [];
    return (categories || []).filter(c =>
      c.parentCategoryId === cat.id ||
      (c.parentCategory && c.parentCategory.id === cat.id)
    );
  };

  // Sous-catégories de la catégorie sélectionnée (niveau 2)
  const subCategories = useMemo(() => getChildren(selectedCategory), [categories, selectedCategory]);

  // Groupes d'attributs cascadants : pour chaque enfant direct de la racine
  // qui a lui-même des enfants, on expose un dropdown (ex: MARQUE → NUGELEC/COOPER)
  const attributeGroups = useMemo(() => {
    if (!selectedCategory) return [];
    return subCategories
      .map((group) => ({
        id: group.id,
        slug: group.slug,
        name: group.name,
        options: getChildren(group),
      }))
      .filter((g) => g.options.length > 0);
  }, [subCategories, categories, selectedCategory]);

  const handleCategorySelect = (categorySlug) => {
    try { console.log('🔍 handleCategorySelect appelé avec:', categorySlug); } catch {}
    const category = categories.find(c => c.slug === categorySlug);
    try {
      console.log('🏷️ Catégorie trouvée:', category);
      console.log('📋 specificFields de la catégorie:', category?.specificFields);
    } catch {}
    
    // ✅ Réinitialiser les filtres spécifiques précédents AVANT de changer la catégorie
    const newFilters = { ...filters };
    const oldSpecificFields = { ...categorySpecificFields };

    // Supprimer les anciens filtres spécifiques de la catégorie précédente
    Object.keys(oldSpecificFields).forEach(field => {
      delete newFilters[field];
    });

    // ✅ Supprimer aussi les filtres cat_* (groupes d'attributs précédents)
    Object.keys(newFilters).forEach((k) => {
      if (k.startsWith('cat_')) delete newFilters[k];
    });

    // Appliquer le nouveau filtre de catégorie
    newFilters.category = categorySlug || '';
    
    // ✅ Mise à jour de la catégorie sélectionnée et des champs spécifiques
    setSelectedCategory(category);
    
    if (category && parseSpecificFields(category.specificFields)) {
      try { console.log('✅ Mise à jour des categorySpecificFields avec:', parseSpecificFields(category.specificFields)); } catch {}
      setCategorySpecificFields(parseSpecificFields(category.specificFields));
    } else {
      try { console.log('❌ Pas de specificFields, réinitialisation'); } catch {}
      setCategorySpecificFields({});
    }
    
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    
    // Synchroniser l'URL
    const next = new URLSearchParams(searchParams);
    
    // Garder la recherche si elle existe
    if (filters.search) {
      next.set('search', filters.search);
    }
    
    if (categorySlug) {
      next.set('category', categorySlug);
    } else {
      next.delete('category');
    }
    
    // Supprimer les anciens paramètres spécifiques de l'URL
    Object.keys(oldSpecificFields).forEach(field => {
      next.delete(field);
    });
    
    setSearchParams(next);
  };

  const toOptions = (values = []) => Array.from(new Set(values.filter(Boolean)))
    .sort((a,b)=>String(a).localeCompare(String(b)))
    .map(v => ({ value: v, label: v }));

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Séparer les filtres par groupe d'attribut catégorie (préfixe "cat_")
      const catFilterSlugs = Object.entries(filters)
        .filter(([k, v]) => k.startsWith('cat_') && v)
        .map(([, v]) => v);

      const baseParams = { ...filters };
      Object.keys(baseParams).forEach((k) => {
        if (k.startsWith('cat_')) delete baseParams[k];
      });

      // Construire le filtre catégorie (root + enfants sélectionnés) en multi-slug
      const categoryChain = [];
      if (filters.category) categoryChain.push(filters.category);
      categoryChain.push(...catFilterSlugs);
      if (categoryChain.length > 0) {
        baseParams.category = categoryChain.join(',');
      }

      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...baseParams,
        status: 'published',
        isVisible: true,
      };
      try {
        console.log('📡 Products.js - Params envoyés au backend:', params);
      } catch {}

      // Nettoyer les paramètres vides
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value === '' || value === undefined || value === null) {
          delete params[key];
        }
        if (key === 'inStock' && value === false) {
          delete params[key];
        }
        if (key === 'search' && (!value || value.trim() === '')) {
          delete params[key];
        }
      });

      const response = await publicProductService.getProducts(params);
      
      setProducts(response.products);
      setPagination(response.pagination);
    } catch (error) {
      console.error('❌ Products.js - Erreur lors du chargement des données:', error);
      console.error('❌ Products.js - Détails de l\'erreur:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: error.config
      });
      setError(`Erreur lors du chargement des produits: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    // Mettre à jour les filtres immédiatement
    setFilters(prev => {
      const updated = { ...prev, [key]: value };
      // Si on change de catégorie, réinitialiser les filtres spécifiques de l'ancienne catégorie
      if (key === 'category' && !value) {
        // Supprimer tous les filtres spécifiques si on enlève la catégorie
        Object.keys(categorySpecificFields).forEach(field => {
          delete updated[field];
        });
      }
      return updated;
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    
    // Synchroniser l'URL
    const next = new URLSearchParams(searchParams);
    const baseKeys = ['search','category','brand','system','subcategory','gasType','detectorType','usage','connectivity','phase','puissance'];
    // Inclure dynamiquement les champs spécifiques de la catégorie courante
    const dynamicSpecificKeys = Object.keys(categorySpecificFields || {});
    const keysToSync = Array.from(new Set([...baseKeys, ...dynamicSpecificKeys]));
    
    // Mettre à jour la valeur pour la clé modifiée
    const updatedFilters = { ...filters, [key]: value };
    
    keysToSync.forEach(k => {
      const v = updatedFilters[k];
      // Gérer les valeurs booléennes, chaînes vides, etc.
      if (v !== undefined && v !== null && v !== '' && String(v).trim() !== '' && v !== false) {
        next.set(k, String(v));
      } else {
        next.delete(k);
      }
    });
    
    // Supprimer les anciens filtres spécifiques qui ne sont plus dans la catégorie actuelle
    if (key === 'category') {
      // Si on change de catégorie, supprimer les anciens paramètres spécifiques
      const allPossibleKeys = ['phase', 'puissance', 'tension', 'puissanceMax', 'puissanceMin', 'type', 'modele'];
      allPossibleKeys.forEach(oldKey => {
        if (!dynamicSpecificKeys.includes(oldKey) && next.has(oldKey)) {
          next.delete(oldKey);
        }
      });
    }
    
    setSearchParams(next);
  };

  const normalizeImageUrl = (raw) => {
    if (!raw) return '';
    let url = String(raw).trim();

    // Google Drive support
    const driveIdMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/) ||
                         url.match(/[?&]id=([a-zA-Z0-9_-]+)/) ||
                         (/^[a-zA-Z0-9_-]{25,}$/.test(url) ? [null, url] : null);
    if (driveIdMatch && driveIdMatch[1]) {
      return `https://drive.google.com/uc?export=view&id=${driveIdMatch[1]}`;
    }

    if (/^https?:\/\//i.test(url)) return url;
    url = url.replace(/\\/g, '/');
    if (!url.startsWith('/')) url = `/${url}`;
    return `${url}`;
  };

  const formatPrice = (price) => {
    if (!price) return '';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  };

  const getCategoryName = (categoryIdOrSlug) => {
    if (!categoryIdOrSlug) return 'Non catégorisé';
    
    let category = categories.find(cat => cat.slug === categoryIdOrSlug);
    if (!category) {
      category = categories.find(cat => cat._id === categoryIdOrSlug);
    }
    
    return category ? category.name : 'Non catégorisé';
  };

  // Compter les filtres actifs
  const getActiveFiltersCount = () => {
    return Object.entries(filters).filter(([k,v]) =>
      !['search','category','sortBy','sortOrder'].includes(k) &&
      v && String(v).trim() !== '' && v !== false
    ).length;
  };

  // Réinitialiser tous les filtres
  const resetAllFilters = () => {
    const resetFilters = {
      ...filters,
      brand: '',
      system: '',
      gasType: '',
      detectorType: '',
      usage: '',
      connectivity: '',
      inStock: false
    };

    // ✅ Réinitialiser aussi les champs spécifiques à la catégorie
    Object.keys(categorySpecificFields).forEach(field => {
      resetFilters[field] = '';
    });

    // ✅ Réinitialiser les filtres cat_* (groupes d'attributs catégorie)
    Object.keys(resetFilters).forEach((k) => {
      if (k.startsWith('cat_')) resetFilters[k] = '';
    });

    setFilters(resetFilters);
    setFiltersDrawerOpen(false);
    setMobileFiltersOpen(false);

    // ✅ Nettoyer l'URL aussi
    const next = new URLSearchParams();
    // Garder seulement la recherche et la catégorie si elles existent
    if (filters.search) next.set('search', filters.search);
    if (filters.category) next.set('category', filters.category);
    setSearchParams(next);
  };

  // Composant des filtres avancés
   const AdvancedFilters = ({ isMobile = false }) => {
    try {
      console.log('🎛️ AdvancedFilters - selectedCategory:', selectedCategory);
      console.log('🎛️ AdvancedFilters - categorySpecificFields:', categorySpecificFields);
      console.log('🎛️ AdvancedFilters - Object.keys(categorySpecificFields):', Object.keys(categorySpecificFields || {}));
    } catch {}
    
    return (
      <div style={{ padding: isMobile ? 0 : 16 }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Collapse 
            ghost
            activeKey={collapseOpen ? ['1'] : []}
            onChange={(keys) => setCollapseOpen(keys.includes('1'))}
          >
             <Panel 
              header={
                <Space>
                  <FilterOutlined />
                  <Text strong>Filtres produits</Text>
                  
                </Space>
              } 
              key="1"
            >
               <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                 {/* 🔎 Recherche et catégorie */}
                 <div>
                   <Space direction="vertical" size="small" style={{ width: '100%' }}>
                     <Input
                       size="large"
                       placeholder="Rechercher par nom ou référence..."
                       value={query}
                       onChange={(e) => setQuery(e.target.value)}
                       onPressEnter={handleSearch}
                       allowClear
                       prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                     />
                     <AntSelect
                       size="large"
                       allowClear
                       placeholder="Toutes les catégories"
                       value={filters.category || undefined}
                       onChange={(val) => handleCategorySelect(val || '')}
                       options={rootCategories.map(c => ({ value: c.slug, label: c.name }))}
                       placement="bottomLeft"
                       getPopupContainer={(trigger) => trigger.parentNode}
                       style={{ width: '100%' }}
                     />
                     <AntButton type="primary" size="large" onClick={handleSearch} block>
                       Rechercher
                     </AntButton>
                   </Space>
                 </div>

                 {/* Groupes d'attributs en cascade (MARQUE, TYPE, NORME, etc.) */}
                 {attributeGroups.length > 0 && (
                   <>
                     <Divider style={{ margin: '8px 0' }} />
                     {attributeGroups.map((group) => (
                       <div key={group.id}>
                         <Text strong style={{ display: 'block', marginBottom: 6, color: '#002c3e', textTransform: 'uppercase', fontSize: 12, letterSpacing: 0.5 }}>
                           {group.name}
                         </Text>
                         <AntSelect
                           size="large"
                           allowClear
                           placeholder={`Choisir ${group.name.toLowerCase()}`}
                           value={filters[`cat_${group.slug}`] || undefined}
                           onChange={(val) => handleFilterChange(`cat_${group.slug}`, val || '')}
                           options={group.options.map((c) => ({ value: c.slug, label: c.name }))}
                           style={{ width: '100%' }}
                           getPopupContainer={(trigger) => trigger.parentNode}
                         />
                       </div>
                     ))}
                   </>
                 )}

                 <Divider style={{ margin: '8px 0' }} />

                {/* ✅ Champs spécifiques à la catégorie */}
                {selectedCategory && 
                 categorySpecificFields && 
                 Object.keys(categorySpecificFields).length > 0 && (
                  <>
                                       
                    {Object.entries(categorySpecificFields).map(([fieldKey, fieldConfig]) => {
                      try { console.log('🎯 Rendu du champ spécifique:', fieldKey, fieldConfig); } catch {}
                      return (
                        <div key={fieldKey}>
                          <Text strong style={{ display: 'block', marginBottom: 8, color: '#666' }}>
                            {fieldConfig.label || fieldKey}
                          </Text>
                          <AntSelect
                            allowClear
                            placeholder={`Sélectionner ${(fieldConfig.label || fieldKey).toLowerCase()}`}
                            value={filters[fieldKey] || undefined}
                            onChange={(val) => handleFilterChange(fieldKey, val || '')}
                            style={{ width: '100%' }}
                            options={fieldConfig.options?.map(option => ({ 
                              value: option, 
                              label: option 
                            })) || []}
                            size="middle"
                          />
                        </div>
                      );
                    })}
                    
                    <Divider style={{ margin: '16px 0' }} />
                    
                   
                  </>
                )}

                
              </Space>
            </Panel>
    
            {/* Bouton de réinitialisation */}
            <div style={{ textAlign: 'center', paddingTop: 16 }}>
              <AntButton 
                icon={<ClearOutlined />}
                onClick={resetAllFilters}
                style={{ width: '100%' }}
                disabled={getActiveFiltersCount() === 0}
              >
                Réinitialiser les filtres
              </AntButton>
            </div>
          </Collapse>
        </Space>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="products-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Chargement des produits...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="container">
        <div style={{ padding: '24px 0' }}>
          {filters.search && filters.search.trim() !== '' ? (
            <div>
              <Title level={3} style={{ marginBottom: 4 }}>
                Résultats de recherche pour "{filters.search}"
              </Title>
              <Text type="secondary">
                {pagination.totalItems} produit{pagination.totalItems > 1 ? 's' : ''} trouvé{pagination.totalItems > 1 ? 's' : ''}
              </Text>
              <div style={{ marginTop: 8 }}>
                <AntButton 
                  type="link" 
                  onClick={() => {
                    setQuery('');
                    setFilters(prev => ({ ...prev, search: '' }));
                  }}
                >
                  Voir tous les produits
                </AntButton>
              </div>
            </div>
          ) : (
            <div>
              <Title level={3} style={{ marginBottom: 4 }}>Nos produits</Title>
              <Text type="secondary">
                Découvrez notre gamme complète de solutions en courant faible
                {pagination.totalItems > 0 && (
                  <span> - {pagination.totalItems} produit{pagination.totalItems > 1 ? 's' : ''} disponible{pagination.totalItems > 1 ? 's' : ''}</span>
                )}
              </Text>
            </div>
          )}
        </div>
        {/* 🟢 Bouton Filtres visible uniquement sur mobile (sous le header) */}
        <div className="mobile-filters-bar">
          <AntButton
            icon={<FilterOutlined />}
            onClick={() => setFiltersDrawerOpen(true)}
            type="default"
          >
            Filtres ({getActiveFiltersCount()})
          </AntButton>
        </div>

        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-triangle"></i>
            {error}
          </div>
        )}

         <section className="results-area" style={{ display: 'flex', gap: 24 }}>
  {/* ✅ Colonne gauche - Filtres */}
          <aside style={{ width: 260, flexShrink: 0 }}>
    <AdvancedFilters />
  </aside>

  {/* ✅ Colonne droite - Résultats produits */}
  <div style={{ flex: 1 }}>
     

    {/* Résultats */}
    <Title level={5} style={{ marginBottom: 24 }}>
      {filters.search 
        ? `Résultats pour "${filters.search}"`
        : 'Tous les produits'}
      <Text type="secondary" style={{ marginLeft: 8 }}>
        ({pagination.totalItems} produit{pagination.totalItems > 1 ? 's' : ''})
      </Text>
    </Title>

    {products.length === 0 ? (
      <div className="no-products">
        <i className="fas fa-box-open"></i>
        <h3>Aucun produit trouvé</h3>
        <p>Essayez de modifier vos critères de recherche</p>
      </div>
    ) : (
      <div className="pp-grid">
        {products.map(product => {
          const imgs = typeof product.images === 'string'
            ? (() => { try { return JSON.parse(product.images); } catch(_) { return {}; } })()
            : (product.images || {});
          const raw = imgs.main || product.imageUrl;
          const driveIdMatch = raw && (
            String(raw).match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/) ||
            String(raw).match(/[?&]id=([a-zA-Z0-9_-]+)/) ||
            (/^[a-zA-Z0-9_-]{25,}$/.test(String(raw)) ? [null, String(raw)] : null)
          );
          const fileId = driveIdMatch && driveIdMatch[1];
          const imgSrc = fileId
            ? `https://drive.google.com/thumbnail?id=${fileId}&sz=w400-h400`
            : normalizeImageUrl(raw);
          const categoryName = product.category?.name || product.categoryName;

          return (
            <Link to={`/produit/${product.slug}`} key={product._id} className="pp-card">
              <div className="pp-card__media">
                {categoryName && <span className="pp-card__tag">{categoryName}</span>}
                <img
                  src={imgSrc}
                  alt={product.name}
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    if (fileId && !e.currentTarget.dataset.retry) {
                      e.currentTarget.dataset.retry = '1';
                      e.currentTarget.src = `https://drive.google.com/uc?export=view&id=${fileId}`;
                    } else {
                      e.currentTarget.style.visibility = 'hidden';
                    }
                  }}
                />
              </div>
              <div className="pp-card__body">
                <h3 className="pp-card__title">{product.name}</h3>
                <p className="pp-card__ref">
                  <i className="fas fa-hashtag"></i>
                  <span>{product.reference}</span>
                </p>
                <div className="pp-card__cta">
                  <span>Voir le produit</span>
                  <i className="fas fa-arrow-right"></i>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    )}

    {/* Pagination simple */}
    {pagination.totalPages > 1 && (
      <SimplePagination
        current={pagination.currentPage}
        total={pagination.totalItems}
        pageSize={pagination.itemsPerPage}
        onChange={(page) => setPagination(prev => ({ ...prev, currentPage: page }))}
        onShowSizeChange={(current, size) => setPagination(prev => ({ 
          ...prev, 
          currentPage: 1, 
          itemsPerPage: size 
        }))}
        showSizeChanger={true}
        showQuickJumper={false}
        showTotal={true}
        size="default"
      />
    )}
  </div>
</section>

      </div>

      {/* 🟢 Drawer mobile pour filtres */}
      <Drawer
        title="Filtres produits"
        placement="left"
        onClose={() => setFiltersDrawerOpen(false)}
        open={filtersDrawerOpen}
        width="85%"
        bodyStyle={{ paddingBottom: 80 }}
      >
        <AdvancedFilters isMobile />
      </Drawer>
    </div>
  );
};

export default Products; 